import React, {useEffect, useState}                 from 'react';
import {loadStripe}                                 from '@stripe/stripe-js';
import {EmbeddedCheckout, EmbeddedCheckoutProvider} from '@stripe/react-stripe-js';
import {useQuery}                                   from "react-query";
import StripeService                                from "../../services/stripe.service";
import {useUser}                                    from "../../context/UserProvider/UserProvider";
import {useLocation, useNavigate}                   from "react-router-dom";
import DOMPurify                                    from "dompurify";
import {Alert}                                      from "@aws-amplify/ui-react";
import {LoadingGif}                                 from "../UIElements";
import CognitoUserService                           from "../../services/cognitoUser.service";
import {useStripeCustomer}                          from "../../requestHooks/useStripeCustomer";

let stripePromise: Promise<any> | null = null;
const KEY = process.env.REACT_APP_PKS;
if (KEY) {
    stripePromise = loadStripe(KEY, {});
}
else {
}


const CheckoutPage: React.FC = () => {
    const {userAttributes, loadingUser} = useUser();
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [checkoutSuccessful, setCheckoutSuccessful] = useState<boolean | null>(null);
    const [customer, setCustomer] = useState<any | null>(null);
    const location = useLocation();
    const navigate = useNavigate();

    const {isFetchingCustomerSubscriptions, fetchCustomerSubscriptions} = useStripeCustomer(
        customer, handleFetchCustomerSubscriptions)

    useEffect(() => {
        // Extract and sanitize session_id from query params
        const queryParams = new URLSearchParams(location.search);
        let rawSessionId = queryParams.get("session_id");

        if (rawSessionId) {
            // Optionally sanitize session_id
            rawSessionId = DOMPurify.sanitize(rawSessionId); // Needs "dompurify" package (optional)
            rawSessionId = rawSessionId.replace(/[^a-zA-Z0-9_-]/g, ""); // Basic sanitization for allowed characters

            console.log("[INFO] Found session_id in query params:", rawSessionId);
            setSessionId(rawSessionId); // Save sanitized session_id in state
        }
    }, [location.search]);

    const {
              isFetching: isLoadingCheckout,
              refetch   : requestCheckoutSession,
          } = useQuery<any[], Error>(
        "query-request-checkout-session",
        async () => {
            console.log(`[DEBUG] Query is being executed by requestCheckoutSession at ${new Date().toISOString()}`);
            const customerId = customer?.customer_id
            return await StripeService.requestCheckoutSession(userAttributes?.email, customerId);
        },
        {
            enabled  : false,
            retry    : 3,
            onSuccess: (res) => {
                handleCheckoutSessionStarted(res);
            },
            onError  : (err: any) => {
                console.error(`[ERROR] Error in requestCheckoutSession:`, err);
            },
        }
    );

    const {
              isFetching: isLoadingCheckoutStatus,
              refetch   : fetchCheckoutStatus,
          } = useQuery<any[], Error>(
        "query-retrieve-checkout-session-status",
        async () => {
            if (sessionId === null) return null;
            return await StripeService.retrieveCheckoutStatus(sessionId);
        },
        {
            enabled  : false,
            retry    : 3,
            onSuccess: (res) => {
                handleFetchSessionStatus(res);
            },
            onError  : (err: any) => {
                console.error(`[ERROR] Error in requestCheckoutSession:`, err);
            },
        }
    );


    const {
              isFetching: isFetchingUpdateUserAttributes,
              refetch   : updateCognitoAttributesForPaidUser,
          } = useQuery<any[], Error>(
        "query-fetch-update-user-attributes",
        async () => {
            if (customer === null) return null;
            return await CognitoUserService.updateUserSubscription(
                userAttributes.email, customer.customer_id, customer.product_id,);
        },
        {
            enabled  : false,
            retry    : 3,
            onSuccess: (res) => {
                console.log("Customer attributes updated successfully: ", res);
                setRedirectCountdown(5);
                console.log("Product ID of active subscription:", customer.product_id);
            },
            onError  : (err: any) => {
                console.error(`[ERROR] Error in fetchCustomerSubscriptions:`, err);
            },
        }
    );

    const handleCheckoutSessionStarted = (res: any) => {
        if (res) {
            let isCheckoutSessionStartedSuccessfully = res["id"];
            if (isCheckoutSessionStartedSuccessfully) {
                const clientSecret = res["client_secret"];
                if (clientSecret) setClientSecret(clientSecret);
            }
        }
    }

    function handleFetchCustomerSubscriptions(res: any) {
        console.log("customer subscriptions result: ", res);

        if (res && Array.isArray(res.data)) {
            // Function to determine if there is an active subscription and retrieve its product_id
            const getActiveSubscriptionProductId = (subscriptions: any[]): string | null => {
                const activeSubscription = subscriptions.find(subscription => subscription.status === "active");
                return activeSubscription?.plan?.id || null; // Access product_id from the plan property
            };

            const product_id = getActiveSubscriptionProductId(res.data); // Access the `data` field here
            console.log("Product ID of active subscription:", product_id);
            if (product_id) {
                // Customer already has an active subscription
                console.log("UPDATING CUSTOMER ATTRIBUTES FOR PAID ACCOUNT");
                setCustomer((prevCustomer: any) => (
                    {
                        ...prevCustomer,
                        product_id: product_id,
                    }
                ));
            }
            else {
                console.warn("No active subscription found.");
            }
        }
        else {
            console.error("Invalid response format or no subscriptions data found.");
        }
    }


    const handleFetchSessionStatus = (res: any) => {
        console.log("session status result: ", res);
        const status = res["status"];
        if (res.status) {
            if (status == 'complete') {
                setCheckoutSuccessful(true);
                const customer = {customer_id: res["customer"], customer_details: res["customer_details"]};
                setCustomer(customer);

            }
            else if (status == 'open') {
                console.log(
                    "The payment failed or was canceled. Remount Checkout so that your customer can try again.");
                setCheckoutSuccessful(false);
                setRedirectCountdown(5);
            }
        }
    }


    useEffect(() => {
        if (!customer) return
        // Trigger only if checkout was successful
        if (checkoutSuccessful && !customer.product_id) {
            fetchCustomerSubscriptions();
        }
        else if (checkoutSuccessful === null && customer && customer.customer_id) {
            requestCheckoutSession();
        }
        else if (customer.product_id) {
            updateCognitoAttributesForPaidUser();
        }
    }, [checkoutSuccessful, customer]);


    const resetCheckoutSession = async () => {
        console.log("[INFO] Resetting checkout session.");
        setSessionId(null);
        setClientSecret(null);
        navigate('/account/checkout', {replace: true})
    };


    useEffect(() => {
        if (loadingUser) return
        const customerId = userAttributes?.["custom:customer_id"];
        if (userAttributes && userAttributes.email && userAttributes?.["custom:accountType"] === 'free'
            && !isLoadingCheckout)
        {
            // check user's subscriptions:
            if (!customerId) {
                console.log('[DEBUG] Refetching requestCheckoutSession manually');
                requestCheckoutSession();
            }
            else {
                let customer = {customer_id: customerId,};
                setCustomer(customer);
            }

        }
    }, [loadingUser]);


    useEffect(() => {
        if (sessionId !== null && !isLoadingCheckoutStatus) {
            fetchCheckoutStatus();
        }

    }, [sessionId])


    useEffect(() => {
        let countdownInterval: NodeJS.Timeout;

        if (redirectCountdown !== null) {
            // Start a countdown timer
            countdownInterval = setInterval(() => {
                setRedirectCountdown((prev) => {
                    if (prev !== null && prev > 1) {
                        return prev - 1; // Decrement countdown
                    }
                    else {
                        // Navigate when countdown finishes
                        clearInterval(countdownInterval);
                        if (checkoutSuccessful === true) {
                            navigate('/account', {replace: true});
                        }
                        else if (checkoutSuccessful === false) resetCheckoutSession();
                        return null;
                    }
                });
            }, 1000);
        }
        // Cleanup interval on component unmount or dependency change
        return () => clearInterval(countdownInterval);

    }, [redirectCountdown]);


    return (
        <div>
            {(
                 !clientSecret || loadingUser
             ) && (
                 <LoadingGif/>
             )}
            {stripePromise && clientSecret && sessionId === null && (
                <EmbeddedCheckoutProvider
                    stripe={stripePromise}
                    options={{clientSecret: clientSecret}}
                >
                    <EmbeddedCheckout/>
                </EmbeddedCheckoutProvider>
            )}

            {sessionId && checkoutSuccessful && (
                <Alert className="m-5 p-5" hasIcon={false} variation="success"
                       heading="Subscription activated!">{redirectCountdown &&
				                                          <div>Redirecting to
					                                          customer portal
					                                          in {redirectCountdown} seconds...</div>}</Alert>
            )}
            {sessionId && checkoutSuccessful === false && (
                <Alert className="m-5 p-5" hasIcon={false} variation="error"
                       heading="Something went wrong">{redirectCountdown &&
				                                       <div>The payment failed or was canceled. Please try
					                                       again. </div>}</Alert>
            )}

        </div>
    );
};

export default CheckoutPage;
